/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { cn } from 'tailwind-variants';
import './Tooltip.react.scss';

const CLICK_OUTSIDE_DELAY_MS = 100;

export default function VueTooltip({
  ref = null,
  className,
  disabled = false,
  position = "top",
  trigger = "hover",
  customPosition,
  onShow,
  onHide,
  slots = {},
  ...restProps
}: any) {
  const [isVisible, setIsVisible] = useState(false);
  const toggleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current);
    };
  }, []);

  function customPositionStyle() {
    if (!customPosition) return {};
    return {
      position: 'absolute' as const,
      ...customPosition
    };
  }

  function showTooltip() {
    if (disabled) return;
    setIsVisible(true);
    onShow?.();
  }

  function hideTooltip() {
    if (disabled) return;
    setIsVisible(false);
    onHide?.();
  }

  function toggleTooltip() {
    if (disabled) return;

    if (toggleTimeoutRef.current) clearTimeout(toggleTimeoutRef.current);

    const nextIsVisible = !isVisible;
    setIsVisible(nextIsVisible);

    if (nextIsVisible) {
      toggleTimeoutRef.current = setTimeout(() => {
        toggleTimeoutRef.current = null;
      }, CLICK_OUTSIDE_DELAY_MS);
      onShow?.();
    } else {
      onHide?.();
    }
  }

  function handleClickOutside() {
    if (toggleTimeoutRef.current || !isVisible) return;
    hideTooltip();
  }

  useImperativeHandle(ref, () => ({
    hideTooltip,
    showTooltip,
  }));

  useEffect(() => {
    if (!isVisible) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        handleClickOutside();
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [isVisible]);

  function handleTargetMouseEnter() {
    if (trigger === 'hover') {
      showTooltip();
    }
  }

  function handleTargetMouseLeave() {
    if (trigger === 'hover') {
      hideTooltip();
    }
  }

  function handleTargetClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (trigger === 'click') {
      toggleTooltip();
    }
  }

  return (
    <div
      {...restProps}
      ref={rootRef}
      className={cn("vue-tooltip scope-aca0e50a", className)}
    >
      <div
        className="vue-tooltip__target scope-aca0e50a"
        onMouseEnter={handleTargetMouseEnter}
        onMouseLeave={handleTargetMouseLeave}
        onClick={handleTargetClick}
        data-testid="vue-tooltip-target"
      >
        {slots.target?.()}
      </div>
      {isVisible && (
        <div
          className={cn(
            "vue-tooltip__content scope-aca0e50a",
            `vue-tooltip__content--${position}`,
            {
              'vue-tooltip__content--custom': customPosition
            }
          )}
          style={customPositionStyle()}
        >
          {slots.content?.()}
        </div>
      )}
    </div>
  );
}
